'use client';

import { useEffect, useMemo, useState } from 'react';

import type { Session } from '@supabase/supabase-js';
import { z } from 'zod';

import { useSupabase } from './use-supabase';

type BetaFeatures = Record<string, boolean>;
const IMPERSONATE_TOKEN = 'nrf_impersonating_token';

const defaultProjectStudioSettingsSchema = z.object({
  addCutMarkersToWav: z.object({ enabled: z.boolean() }).optional(),
  pauseAdjustment: z
    .object({
      maxPauseLengthInSameParagraph: z.number(),
      maxPauseLengthInDifferentParagraphs: z.number(),
    })
    .optional(),
});

type DefaultProjectStudioSettings = z.infer<
  typeof defaultProjectStudioSettingsSchema
>;

export function useNrfUserSettings(): {
  betaFeatures: BetaFeatures;
  defaultProjectStudioSettings: DefaultProjectStudioSettings;
  isImpersonated: boolean;
} {
  const supabase = useSupabase();
  const [user, setUser] = useState<Session['user'] | null>(null);
  const [isTokenImpersonated, setIsTokenImpersonated] = useState<boolean>(() =>
    isImpersonatedToken(),
  );

  useEffect(() => {
    let isActive = true;

    const loadSession = async () => {
      const [sessionResponse, userResponse] = await Promise.all([
        supabase.auth.getSession(),
        supabase.auth.getUser(),
      ]);

      if (!isActive) {
        return;
      }

      const nextSession = sessionResponse.data.session ?? null;
      const nextUser = userResponse.data.user ?? nextSession?.user ?? null;

      setUser(nextUser);
      setIsTokenImpersonated(
        isImpersonatedToken(nextSession?.access_token ?? null),
      );
    };

    void loadSession();

    return () => {
      isActive = false;
    };
  }, [supabase.auth]);

  const betaFeatures = useMemo(() => normalizeBetaFeatures(user), [user]);
  const defaultProjectStudioSettings = useMemo(
    () => normalizeDefaultProjectStudioSettings(user),
    [user],
  );
  const isImpersonated = useMemo(
    () => isTokenImpersonated,
    [isTokenImpersonated],
  );

  return { betaFeatures, defaultProjectStudioSettings, isImpersonated };
}

function normalizeBetaFeatures(user: Session['user'] | null): BetaFeatures {
  const raw = user?.app_metadata?.nrf_beta_features;

  if (!raw || typeof raw !== 'object') {
    return {};
  }

  return Object.entries(raw as Record<string, unknown>).reduce(
    (acc, [key, value]) => {
      acc[key] = value === true;
      return acc;
    },
    {} as BetaFeatures,
  );
}

function normalizeDefaultProjectStudioSettings(
  user: Session['user'] | null,
): DefaultProjectStudioSettings {
  const raw = user?.user_metadata?.default_project_studio_settings;
  if (!raw || typeof raw !== 'object') {
    return {};
  }

  const result = defaultProjectStudioSettingsSchema.safeParse(raw);
  return result.success ? result.data : {};
}

function isImpersonatedToken(currentAccessToken: string | null = null) {
  if (typeof window === 'undefined') {
    return false;
  }

  const storedToken = window.localStorage.getItem(IMPERSONATE_TOKEN);

  // We only consider the user impersonated when the stored token matches
  // the currently active access token.
  if (!storedToken || !currentAccessToken) {
    return false;
  }

  return storedToken === currentAccessToken;
}

export function setImpersonateFlag(accessToken: string) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(IMPERSONATE_TOKEN, accessToken);
}
